import { api } from './http';
import type { AuditEntry, ModuleItem, ModuleSwitches, PermissionGroup, Pass, PassInput, Role, Settings, TeamMember } from './types';

export const passes = {
  list: (signal?: AbortSignal) => api.get<{ items: Pass[] }>('/passes', signal),
  create: (f: PassInput) => api.post<{ item: Pass }>('/passes', f),
  update: (id: number, f: PassInput) => api.put<{ item: Pass }>(`/passes/${id}`, f),
  remove: (id: number) => api.del<{ ok: true }>(`/passes/${id}`),
};

export const roles = {
  list: (signal?: AbortSignal) => api.get<{
    roles: Role[]; groups: PermissionGroup[]; owner_locked: string[];
  }>('/roles', signal),
  create: (f: { name: string; permissions: string[] }) => api.post<{ role: Role }>('/roles', f),
  update: (id: number, f: { name: string; permissions: string[] }) => api.put<{ role: Role }>(`/roles/${id}`, f),
  remove: (id: number) => api.del<{ ok: true }>(`/roles/${id}`),
};

export const team = {
  list: (signal?: AbortSignal) => api.get<{
    team: TeamMember[]; roles: Role[]; me: number; owner_count: number; audit: AuditEntry[];
  }>('/team', signal),
  create: (f: { name: string; email: string; password: string; role_id: number }) =>
    api.post<{ member: TeamMember }>('/team', f),
  update: (id: number, f: { name?: string; email?: string; role_id?: number; state?: string; password?: string }) =>
    api.put<{ member: TeamMember }>(`/team/${id}`, f),
  remove: (id: number) => api.del<{ ok: true }>(`/team/${id}`),
};

export const settings = {
  load: (signal?: AbortSignal) => api.get<{ settings: Settings; keys: string[]; paymentWebhookUrl?: string }>('/settings', signal),
  save: (values: Settings) => api.put<{ settings: Settings; telegram?: { ok: boolean; bot?: string; error?: string } }>('/settings', values),
};

export interface TelegramStatus {
  configured: boolean; connected?: boolean; bot?: string; chat_id: string; mode: 'draft' | 'publish';
  seen: { id: string; title: string } | null; url: string; calls: number; pending?: number; last_error?: string;
}
/** The Telegram feed behind Trade Calls: is the bot connected, and what has it heard. */
export const telegram = {
  status: (signal?: AbortSignal) => api.get<TelegramStatus>('/settings/telegram', signal),
  connect: () => api.post<{ ok: boolean; bot: string }>('/settings/telegram/connect', {}),
};

export type SectionMode = 'auto' | 'on' | 'off';
export interface SectionRow {
  key: string; label: string; mode: SectionMode; count: number; has_content: boolean; showing: boolean; what: string;
}
/** Which News & Analysis sections members and visitors can see. */
export const sections = {
  load: (signal?: AbortSignal) => api.get<{ sections: SectionRow[] }>('/settings/sections', signal),
  save: (patch: Record<string, SectionMode>) => api.put<{ sections: SectionRow[] }>('/settings/sections', { sections: patch }),
};

/** Settings → Modules: the switches, and where a switched-off module sends people. */
export const modules = {
  load: (signal?: AbortSignal) => api.get<{
    modules: ModuleItem[]; platforms: string[]; state: Record<string, ModuleSwitches>;
  }>('/settings/modules', signal),
  /** 400 with the reason when a link is not a full https:// address. */
  save: (state: Record<string, ModuleSwitches>) =>
    api.put<{ state: Record<string, ModuleSwitches> }>('/settings/modules', { state }),
};
